import React, { useState } from "react";
import {
  ElementType,
  ElementViewProps,
  FluidElementLayout,
  FormSettings,
} from "@/shared/types";
import { ButtonView } from "../Button";
import { useForm } from "react-hook-form";
import FieldView from "./FieldView";
import FeedbackMessage from "./components/FeedbackMessage/FeedbackMessage";
import Spinner from "@/shared/Components/Spinner/Spinner";
import styles from "./FormView.module.scss";
import {
  getFormContent,
  getFormResolver,
  handlePostSubmitRedirect,
} from "./form-helpers";

export interface FormViewProps extends ElementViewProps {}

const FormView: React.FC<FormViewProps> = ({
  element,
  wuiltContext,
  sectionId,
  sectionValueId,
  text,
}) => {
  const base = wuiltContext?.base;
  const router = base?.useRouter?.();
  const wuiltProps = wuiltContext?.props;
  const formSettings = element?.settings as FormSettings;
  const [isRequestFailed, setIsRequestFailed] = useState(false);
  const [formSubmitSucceeded, setFormSubmitSucceeded] = useState(false);
  const useFormProps = useForm({ resolver: getFormResolver(formSettings) });
  const { isSubmitting, isSubmitSuccessful, isSubmitted, isValid } =
    useFormProps.formState;

  const onSubmit = async (formValues: any) => {
    setIsRequestFailed(false);
    setFormSubmitSucceeded(false);
    const formContent = getFormContent(formSettings, formValues, text!);
    if (!wuiltProps?.getFormSubmitUrl) return;
    try {
      const res = await fetch(
        wuiltProps?.getFormSubmitUrl(formSettings?.formId),
        {
          credentials: "include",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          method: "post",
          body: JSON.stringify({
            subject: formSettings?.formName || "Unnamed Form",
            recipients: formSettings?.notifications?.emails || [
              wuiltProps?.userEmail,
            ],
            fields: formContent,
          }),
        }
      );
      if (res.ok) useFormProps?.reset();
      if (res.status === 429) {
        setIsRequestFailed(true);
        setFormSubmitSucceeded(false);
        alert(
          "You have reached the limit of submitting the form, Please try again later after 24 hours."
        );
      }
      if (!res.ok) throw new Error();
      setFormSubmitSucceeded(true);
      handlePostSubmitRedirect(formSettings, router);
    } catch (_error) {
      setFormSubmitSucceeded(false);
      setIsRequestFailed(true);
    }
  };

  const isSubmitFailed =
    isRequestFailed && !formSubmitSucceeded && isSubmitted && isValid;

  const success = formSubmitSucceeded && isSubmitSuccessful;

  return (
    <form
      id={element?.id}
      data-test="FormView"
      onSubmit={useFormProps?.handleSubmit(onSubmit)}
      className={styles.FormFieldsContainer}
    >
      {formSettings?.fields?.map((field) => (
        <FieldView
          formId={formSettings?.formId!}
          uploadFileUrl={wuiltProps?.uploadFileUrl!}
          sectionId={sectionId}
          sectionValueId={sectionValueId}
          key={field.id}
          field={field}
          useFormProps={useFormProps}
          isSubmitting={isSubmitting}
          formSubmitSucceeded={success}
          text={text!}
          elementId={element?.id}
          wuiltContext={wuiltContext!}
        />
      ))}
      <div>
        <ButtonView
          text={text}
          isDisabled={isSubmitting}
          element={{
            id: "Button:id",
            layout: {} as FluidElementLayout,
            settings: formSettings?.formButton!,
            type: ElementType.Button,
          }}
          wuiltContext={wuiltContext!}
        />
        <span>{isSubmitting && <Spinner />}</span>
      </div>
      <FeedbackMessage
        formSettings={formSettings}
        success={success}
        failure={isSubmitFailed}
        resetForm={() => {
          setIsRequestFailed(false);
          useFormProps?.reset();
        }}
        text={text!}
      />
    </form>
  );
};

export { FormView };
